import { useEffect, useRef, useState } from "react";
import { Download, MoreHorizontal, RefreshCw, Upload } from "lucide-react";
import { useDemoStore } from "@/store/useDemoStore";
import { useToast } from "@/hooks/use-toast";

export function UtilitiesMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const reset = useDemoStore((s) => s.resetDemo);
  const exportState = useDemoStore((s) => s.exportState);
  const importState = useDemoStore((s) => s.importState);
  const { toast } = useToast();

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function doExport() {
    const json = exportState();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `certicore-state-${stamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
    toast({ title: "Estado exportado", description: "Se descargó el archivo JSON." });
  }

  function doImport() {
    fileRef.current?.click();
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      const res = importState(String(r.result ?? ""));
      if (res.ok) {
        toast({ title: "Estado importado", description: "La aplicación se actualizó con los datos del archivo." });
      } else {
        toast({ title: "Error al importar", description: res.error ?? "Archivo inválido", variant: "destructive" });
      }
      setOpen(false);
      if (fileRef.current) fileRef.current.value = "";
    };
    r.readAsText(file);
  }

  function doReset() {
    if (window.confirm("¿Restablecer la demo? Se perderán los cambios locales.")) {
      reset();
      setOpen(false);
      toast({ title: "Demo restablecida", description: "Datos iniciales recargados." });
    }
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input ref={fileRef} type="file" accept="application/json" onChange={onFile} style={{ display: "none" }} />
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="button-utilities"
        title="Utilidades"
        style={{
          width: 32, height: 32, borderRadius: 8,
          border: "1px solid var(--cc-gray-200)",
          background: "white",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <MoreHorizontal size={15} color="var(--cc-gray-600)" />
      </button>
      {open && (
        <div
          style={{
            position: "absolute", top: 38, right: 0, zIndex: 60,
            background: "white",
            border: "1px solid var(--cc-gray-200)",
            borderRadius: 10,
            boxShadow: "0 12px 28px -8px rgba(15,23,42,0.18)",
            padding: 6, minWidth: 220,
          }}
          data-testid="utilities-menu"
        >
          <MenuItem icon={<RefreshCw size={14} />} label="Restablecer demo" onClick={doReset} />
          <MenuItem icon={<Download size={14} />} label="Exportar JSON" onClick={doExport} />
          <MenuItem icon={<Upload size={14} />} label="Importar JSON" onClick={doImport} />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      data-testid={`utility-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        width: "100%", padding: "8px 10px",
        background: "transparent", border: "none",
        borderRadius: 6, cursor: "pointer",
        fontSize: 13, color: "var(--cc-gray-800)",
        textAlign: "left",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--cc-gray-100)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <span style={{ color: "var(--cc-gray-600)", display: "flex" }}>{icon}</span>
      {label}
    </button>
  );
}
